import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import toastError from "../../errors/toastError";

export const mergeMessages = (current, incoming) => {
  const byId = new Map(current.map(message => [message.id, message]));
  incoming.forEach(message => byId.set(message.id, message));
  return [...byId.values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt) || a.id - b.id);
};
const mergeChat = (current, next) => ({ ...current, ...next, users: next.users?.map(member => ({ ...member, user: member.user || current.users?.find(old => old.userId === member.userId)?.user })) || current.users });

export default function useInternalChat({ user, socket, uuid, onRemoved }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState({ searchParam: "", pageNumber: 1 });
  const [revision, setRevision] = useState(0);
  const [selectionRevision, setSelectionRevision] = useState(0);
  const [list, setList] = useState({ records: [], count: 0, hasMore: false, loading: true, error: false });
  const [chat, setChat] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageInfo, setMessageInfo] = useState({ hasMore: false, loading: false, error: false });
  const [sending, setSending] = useState(false);
  const chatRef = useRef(null);
  const activeMessages = useRef(null);
  const nextPage = useRef(1);
  const fetchingMessages = useRef(false);
  const sendingRef = useRef(false);
  const mounted = useRef(true);
  const readBusy = useRef(new Set());
  const readPending = useRef(new Set());
  const removedRef = useRef(onRemoved);
  removedRef.current = onRemoved;
  chatRef.current = chat;
  useEffect(() => () => { mounted.current = false; }, []);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  const reloadChat = useCallback(() => setSelectionRevision(value => value + 1), []);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(current => current.searchParam === search.trim() ? current : { searchParam: search.trim(), pageNumber: 1 }), 350);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    let live = true;
    setList(current => ({ ...current, loading: true, error: false }));
    api.get("/chats", { params: query, timeout: 20000 }).then(({ data }) => {
      if (!live) return;
      const lastPage = Math.max(1, Math.ceil(data.count / 20));
      if (query.pageNumber > lastPage) { setQuery(current => ({ ...current, pageNumber: lastPage })); return; }
      setList({ ...data, loading: false, error: false });
    }).catch(() => { if (live) setList(current => ({ ...current, loading: false, error: true })); });
    return () => { live = false; };
  }, [query, revision, user.companyId]);

  const markRead = useCallback(async chatId => {
    if (readBusy.current.has(chatId)) { readPending.current.add(chatId); return; }
    readBusy.current.add(chatId);
    try {
      do {
        readPending.current.delete(chatId);
        const { data } = await api.post(`/chats/${chatId}/read`, { userId: user.id });
        if (!mounted.current) return;
        setList(current => ({ ...current, records: current.records.map(item => item.id === chatId ? mergeChat(item, data) : item) }));
      } while (readPending.current.has(chatId));
    } catch (err) { if (mounted.current) toastError(err); }
    finally { readBusy.current.delete(chatId); readPending.current.delete(chatId); }
  }, [user.id]);

  useEffect(() => {
    let live = true;
    setChat(null); setChatError(false);
    if (!uuid) { setChatLoading(false); return undefined; }
    setChatLoading(true);
    api.get(`/chats/${uuid}`, { timeout: 20000 }).then(({ data }) => {
      if (!live) return;
      setChat(data); setChatLoading(false); markRead(data.id);
    }).catch(() => { if (live) { setChatError(true); setChatLoading(false); } });
    return () => { live = false; };
  }, [uuid, selectionRevision, user.companyId, markRead]);

  const chatId = chat?.id;
  const loadMessages = useCallback(async () => {
    const key = activeMessages.current;
    if (!key || fetchingMessages.current) return;
    const pageNumber = nextPage.current;
    fetchingMessages.current = true;
    setMessageInfo(current => ({ ...current, loading: true, error: false }));
    try {
      const { data } = await api.get(`/chats/${key.id}/messages`, { params: { pageNumber }, timeout: 20000 });
      if (activeMessages.current !== key) return;
      nextPage.current = pageNumber + 1;
      setMessages(current => mergeMessages(current, data.records));
      setMessageInfo({ hasMore: data.hasMore, loading: false, error: false });
    } catch (err) {
      if (activeMessages.current === key) setMessageInfo(current => ({ ...current, loading: false, error: true }));
    } finally { if (activeMessages.current === key) fetchingMessages.current = false; }
  }, []);
  useEffect(() => {
    const key = chatId ? { id: chatId } : null;
    activeMessages.current = key; nextPage.current = 1; fetchingMessages.current = false;
    setMessages([]); setMessageInfo({ hasMore: false, loading: false, error: false });
    if (key) loadMessages();
    return () => { activeMessages.current = null; };
  }, [chatId, loadMessages]);

  useEffect(() => {
    if (typeof socket?.on !== "function") return undefined;
    let timer;
    const scheduleRefresh = () => { if (!timer) timer = setTimeout(() => { timer = null; refresh(); }, 350); };
    const changed = data => {
      const record = data.record || data.chat;
      if (data.action === "delete" || data.action === "remove") {
        if (String(chatRef.current?.id) === String(data.id)) removedRef.current();
        scheduleRefresh(); return;
      }
      if (record?.users && !record.users.some(member => Number(member.userId) === Number(user.id))) return;
      if (record && record.id === chatRef.current?.id) {
        setChat(current => current?.id === record.id ? mergeChat(current, record) : current);
        if (data.action === "new-message" && data.newMessage) {
          setMessages(current => mergeMessages(current, [data.newMessage]));
          if (Number(data.newMessage.senderId) !== Number(user.id)) markRead(record.id);
        }
      }
      if (record) setList(current => ({ ...current, records: current.records.map(item => item.id === record.id ? mergeChat(item, record) : item) }));
      // A read receipt does not change ordering or membership.
      if (["create", "new-message", "update"].includes(data.action) && data.record) scheduleRefresh();
      if (data.action === "new-message") scheduleRefresh();
    };
    const channels = [`company-${user.companyId}-chat`, `company-${user.companyId}-chat-user-${user.id}`];
    channels.forEach(channel => socket.on(channel, changed));
    return () => { clearTimeout(timer); channels.forEach(channel => socket.off(channel, changed)); };
  }, [socket, user.id, user.companyId, refresh, markRead]);

  const send = useCallback(async message => {
    const target = chatRef.current;
    if (!target || sendingRef.current || !message.trim()) return false;
    sendingRef.current = true; setSending(true);
    try {
      const { data } = await api.post(`/chats/${target.id}/messages`, { message: message.trim() });
      if (!mounted.current) return true;
      if (chatRef.current?.id === target.id) setMessages(current => mergeMessages(current, [data]));
      refresh(); return true;
    } catch (err) { if (mounted.current) toastError(err); return false; }
    finally { sendingRef.current = false; if (mounted.current) setSending(false); }
  }, [refresh]);

  return { ...list, ...query, search, setSearch, setPage: pageNumber => setQuery(current => ({ ...current, pageNumber })), refresh, chat, chatLoading, chatError, reloadChat, messages, messageInfo, loadMessages, send, sending };
}
